import React from 'react'
import { useTranslation } from 'react-i18next'

import PieChart, { hasChartData } from '../components/common/PieChart'
import BarChart from '../components/common/BarChart'
import LineChart from '../components/common/LineChart'
import AnalysisEmptyState from '../components/common/AnalysisEmptyState'
import {
  AnalysisTone,
  barFill,
} from '../components/common/analysisLayout'
import brandLogo from '../views/layouts/images/logo1-close.png'
import '../components/common/maturity-analysis.css'
import './maturity-analysis-pdf.css'
import './analysis-pdf.css'

/* eslint-disable @typescript-eslint/no-explicit-any */
export type AnalysisPdfChart = {
  key: string
  title: string
  kind: 'pie' | 'bar' | 'line'
  data: any
}

export type AnalysisPdfKpi = {
  label: string
  value: string
  desc?: string
  tone: AnalysisTone
  progress?: number
}

export type AnalysisPdfSection = {
  label: string
  charts: AnalysisPdfChart[]
}

export type AnalysisPdfTable = {
  label: string
  title: string
  hint?: string
  headers: string[]
  rows: string[][]
  numericLast?: boolean
  /** CSS grid-template-columns override */
  columns?: string
}

export type AnalysisPdfExtra = {
  label: string
  title: string
  node: React.ReactNode
}

export type AnalysisPdfReportProps = {
  eyebrow: string
  title: string
  subtitle: string
  computedOn: string
  footerTitle: string
  kpis?: AnalysisPdfKpi[]
  sections?: AnalysisPdfSection[]
  tables?: AnalysisPdfTable[]
  extra?: AnalysisPdfExtra
}

function ChartCard({
  chart,
  empty = false,
}: {
  chart: AnalysisPdfChart
  empty?: boolean
}) {
  return (
    <section
      className={`ma-pdf-card ap-pdf-chart${chart.kind === 'line' ? ' is-wide' : ''}`}
    >
      <div className="ma-pdf-card-head ma-pdf-card-head--teal">
        <h2>{chart.title}</h2>
      </div>
      <div className="ma-pdf-card-body">
        {empty ? (
          <AnalysisEmptyState />
        ) : chart.kind === 'pie' ? (
          <PieChart
            embedded
            title={chart.title}
            chartTitle={chart.title}
            data={chart.data}
            itemRow={2}
          />
        ) : chart.kind === 'bar' ? (
          <BarChart
            embedded
            title={chart.title}
            chartTitle={chart.title}
            data={chart.data}
          />
        ) : (
          <LineChart embedded title={chart.title} data={chart.data} />
        )}
      </div>
    </section>
  )
}

/** Two charts per row; line charts take a full row. */
function chunkRows(charts: AnalysisPdfChart[]) {
  const rows: AnalysisPdfChart[][] = []
  let pending: AnalysisPdfChart[] = []
  charts.forEach((chart) => {
    if (chart.kind === 'line') {
      if (pending.length) rows.push(pending)
      pending = []
      rows.push([chart])
      return
    }
    pending.push(chart)
    if (pending.length === 2) {
      rows.push(pending)
      pending = []
    }
  })
  if (pending.length) rows.push(pending)
  return rows
}

function ChartSection({ section }: { section: AnalysisPdfSection }) {
  const withData = section.charts.filter((c) => hasChartData(c.data))
  if (!section.charts.length) return null
  const empty = withData.length === 0
  const visible = empty ? section.charts : withData
  return (
    <>
      <p className="ma-pdf-section-label avoid-break">{section.label}</p>
      <div className="ap-pdf-grid">
        {chunkRows(visible).map((row) => (
          <div
            className={`ap-pdf-row${row.length === 1 && (row[0].kind === 'line' || visible.length === 1) ? ' is-single' : ''}`}
            key={row.map((c) => c.key).join('-')}
          >
            {row.map((chart) => (
              <ChartCard chart={chart} key={chart.key} empty={empty} />
            ))}
          </div>
        ))}
      </div>
    </>
  )
}

function TableSection({ table }: { table: AnalysisPdfTable }) {
  if (!table.rows.length) return null
  const cols = table.headers.length
  const template = table.columns
    ? table.columns
    : cols <= 2
      ? table.numericLast
        ? 'minmax(0, 1fr) 160px'
        : 'minmax(0, 1fr) minmax(0, 2fr)'
      : `minmax(0, 1.4fr) repeat(${cols - 1}, minmax(0, 1fr))`
  return (
    <>
      <p className="ma-pdf-section-label avoid-break">{table.label}</p>
      <section className="ma-pdf-card ap-pdf-table-card">
        <div className="ma-pdf-card-head ma-pdf-card-head--navy">
          <h2>{table.title}</h2>
          {table.hint ? (
            <span className="ma-pdf-legend">{table.hint}</span>
          ) : null}
        </div>
        <div
          className={`ap-pdf-table${table.numericLast ? ' is-numeric-last' : ''}`}
          role="table"
        >
          <div
            className="ap-pdf-trow ap-pdf-thead"
            role="row"
            style={{ gridTemplateColumns: template }}
          >
            {table.headers.map((h) => (
              <span key={h} role="columnheader">
                {h}
              </span>
            ))}
          </div>
          {table.rows.map((row, i) => (
            <div
              className="ap-pdf-trow"
              role="row"
              key={i}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, j) => (
                <span key={j} role="cell">
                  {cell}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>
    </>
  )
}

const AnalysisPdfReport = React.forwardRef<
  HTMLDivElement,
  AnalysisPdfReportProps
>(function AnalysisPdfReport(
  {
    eyebrow,
    title,
    subtitle,
    computedOn,
    footerTitle,
    kpis = [],
    sections = [],
    tables = [],
    extra,
  },
  ref
) {
  const { t } = useTranslation()

  return (
    <div ref={ref} className="ma-pdf ap-pdf" data-pdf-ready="true">
      <header className="ma-pdf-hero avoid-break">
        <div className="ma-pdf-hero-top">
          <div className="ma-pdf-brand">
            <span className="ma-pdf-brand-mark">
              <img src={brandLogo} alt="" />
            </span>
            <div>
              <span className="ma-pdf-brand-name">INCYIA</span>
              <span className="ma-pdf-brand-tag">
                {t('analysis.maturityAnalysis.pdfDocType')}
              </span>
            </div>
          </div>
          <div className="ma-pdf-date-chip">
            <span className="ma-pdf-date-label">
              {t('analysis.maturityAnalysis.pdfReportDate')}
            </span>
            <strong>{computedOn}</strong>
          </div>
        </div>
        <p className="ma-pdf-eyebrow">{eyebrow}</p>
        <h1 className="ma-pdf-title">{title || '—'}</h1>
        <p className="ma-pdf-sub">{subtitle}</p>
      </header>

      {kpis.length > 0 ? (
        <>
          <p className="ma-pdf-section-label avoid-break">
            {t('analysis.maturityAnalysis.pdfSectionKpis')}
          </p>
          <section
            className={`ma-pdf-kpi-grid ap-pdf-kpis${kpis.length >= 3 ? ' is-3' : ''}${kpis.length === 1 ? ' is-1' : ''}`}
          >
            {kpis.map((kpi) => {
              const pct =
                kpi.progress === undefined
                  ? undefined
                  : Math.min(100, Math.max(0, Number(kpi.progress) || 0))
              return (
                <div
                  className={`ma-pdf-kpi ma-pdf-kpi--${kpi.tone}`}
                  key={kpi.label}
                >
                  <p className="ma-pdf-kpi-label ap-pdf-kpi-label">
                    {kpi.label}
                  </p>
                  <div className={`ma-pdf-kpi-value is-${kpi.tone}`}>
                    {kpi.value}
                  </div>
                  {pct !== undefined ? (
                    <div className="ma-pdf-bar">
                      <span
                        style={{ width: `${pct}%`, background: barFill(kpi.tone) }}
                      />
                    </div>
                  ) : null}
                  {kpi.desc ? (
                    <p className="ma-pdf-kpi-desc">{kpi.desc}</p>
                  ) : null}
                </div>
              )
            })}
          </section>
        </>
      ) : null}

      {sections.map((section) => (
        <ChartSection section={section} key={section.label} />
      ))}

      {extra ? (
        <>
          <p className="ma-pdf-section-label avoid-break">{extra.label}</p>
          <section className="ma-pdf-card ap-pdf-extra">
            <div className="ma-pdf-card-head ma-pdf-card-head--navy">
              <h2>{extra.title}</h2>
            </div>
            <div className="ma-pdf-card-body">{extra.node}</div>
          </section>
        </>
      ) : null}

      {tables.map((table) => (
        <TableSection table={table} key={table.title} />
      ))}

      <footer className="ma-pdf-footer avoid-break">
        <div className="ma-pdf-footer-brand">
          <span className="ma-pdf-footer-dot" />
          {t('analysis.pdf.footerBrand', { title: footerTitle })}
        </div>
        <span>
          {t('analysis.maturityAnalysis.pdfGenerated', { date: computedOn })}
        </span>
      </footer>
    </div>
  )
})

export default AnalysisPdfReport
